// Кнопка «Включить уведомления» в шапке AppShell.
// Состояния:
//   - not-supported: браузер не даёт push (Firefox mobile, старый Safari) - скрываем.
//   - denied: разрешение отклонено - показываем tooltip как включить.
//   - default (permission='default'): показываем «Включить уведомления».
//   - subscribed: показываем «Отключить уведомления» + зелёная точка.
//   - not-ready: сервер не отдал VAPID (ключи не заданы) - показываем serverError.

import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { BellIcon, BellSlashIcon } from '@phosphor-icons/react';
import { fetchVapid, subscribePush, unsubscribePush } from '@/features/push/api';

type PushState =
  | 'loading'
  | 'not-supported'
  | 'not-ready'
  | 'denied'
  | 'default'
  | 'subscribed';

/** Base64URL → Uint8Array (VAPID applicationServerKey). */
function urlB64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

export function PushToggle() {
  const [state, setState] = useState<PushState>('loading');
  const [busy, setBusy] = useState(false);
  const [vapidKey, setVapidKey] = useState<string | null>(null);

  useEffect(() => {
    void resolveState();
  }, []);

  async function resolveState() {
    if (typeof window === 'undefined') return;
    if (!('serviceWorker' in navigator) || !('PushManager' in window) || !('Notification' in window)) {
      setState('not-supported');
      return;
    }
    try {
      const vapid = await fetchVapid();
      if (!vapid.ready || !vapid.publicKey) {
        setState('not-ready');
        return;
      }
      setVapidKey(vapid.publicKey);
    } catch {
      setState('not-ready');
      return;
    }
    if (Notification.permission === 'denied') {
      setState('denied');
      return;
    }
    if (Notification.permission === 'granted') {
      const reg = await navigator.serviceWorker.ready.catch(() => null);
      const sub = await reg?.pushManager.getSubscription().catch(() => null);
      setState(sub ? 'subscribed' : 'default');
      return;
    }
    setState('default');
  }

  async function enable() {
    if (busy || !vapidKey) return;
    setBusy(true);
    try {
      const permission = await Notification.requestPermission();
      if (permission !== 'granted') {
        setState(permission === 'denied' ? 'denied' : 'default');
        return;
      }
      const reg = await navigator.serviceWorker.ready;
      // TypeScript 7 требует BufferSource с ArrayBuffer, Uint8Array из urlB64ToUint8Array
      // возвращает ArrayBufferLike - явно кастуем к нужному типу.
      const key = urlB64ToUint8Array(vapidKey);
      const sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: key.buffer as ArrayBuffer,
      });
      const raw = sub.toJSON();
      if (!raw.endpoint || !raw.keys?.p256dh || !raw.keys.auth) {
        toast.error('Браузер не отдал полный ключ подписки');
        return;
      }
      await subscribePush({
        endpoint: raw.endpoint,
        keys: { p256dh: raw.keys.p256dh, auth: raw.keys.auth },
      });
      setState('subscribed');
      toast.success('Уведомления включены');
    } catch (e) {
      toast.error(`Не удалось подписаться: ${(e as Error).message}`);
    } finally {
      setBusy(false);
    }
  }

  async function disable() {
    if (busy) return;
    setBusy(true);
    try {
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.getSubscription();
      if (sub) {
        await unsubscribePush(sub.endpoint).catch(() => undefined);
        await sub.unsubscribe();
      }
      setState('default');
      toast.success('Уведомления отключены');
    } catch (e) {
      toast.error(`Ошибка: ${(e as Error).message}`);
    } finally {
      setBusy(false);
    }
  }

  if (state === 'loading' || state === 'not-supported' || state === 'not-ready') {
    return null;
  }
  if (state === 'denied') {
    return (
      <button
        type="button"
        className="inline-flex items-center gap-1.5 rounded-md border border-red/40 bg-red/10 px-3 py-1.5 text-xs text-red"
        title="Разрешение на уведомления отклонено. Разблокируйте в настройках браузера: замочек рядом с URL → Уведомления → Разрешить."
      >
        <BellSlashIcon weight="bold" className="size-3.5" />
        Уведомления заблокированы
      </button>
    );
  }
  // Состояние показывает сам колокольчик - залитый значит «уведомления включены».
  // Подпись-состояние рядом с переключателем не пишем: она дублирует контрол и спорит с ним.
  if (state === 'subscribed') {
    return (
      <button
        type="button"
        onClick={disable}
        disabled={busy}
        className="grid size-9 place-items-center rounded-md bg-green/10 text-green transition-colors hover:bg-green/15 disabled:opacity-60"
        title="Уведомления включены. Нажмите, чтобы выключить"
        aria-label="Выключить уведомления"
        aria-pressed
      >
        <BellIcon weight="fill" className="size-4" />
      </button>
    );
  }
  return (
    <button
      type="button"
      onClick={enable}
      disabled={busy}
      className="grid size-9 place-items-center rounded-md text-muted transition-colors hover:bg-panel2 hover:text-text disabled:opacity-60"
      title="Включить уведомления о новых обращениях - придут, даже когда вкладка закрыта"
      aria-label="Включить уведомления"
      aria-pressed={false}
    >
      <BellIcon weight="bold" className="size-4" />
    </button>
  );
}
